import type { LawyerCardData } from '@/Types/lawyer';

/**
 * Server controllers pass the result of App\Support\LawyerPresenter::card
 * as a richer JSON shape. Cards still take the trimmer LawyerCardData
 * shape defined in `@/Types/lawyer`. This adapter sits at the page
 * boundary so the card components stay design-system-only.
 */
export interface ServerLawyerCard {
    id: number;
    slug: string;
    name: string;
    firm: string | null;
    city: string | null;
    state: string | null;
    stateSlug: string | null;
    stateAbbr: string | null;
    phone: string | null;
    yearsLicensed: number | null;
    languages: string[];
    freeConsultation: boolean;
    feeStructure: string | null;
    featured: boolean;
    reviewsAvg: number | null;
    reviewsCount: number;
    practiceAreas: Array<{ slug: string; name: string }>;
    primaryPracticeArea: { slug: string; name: string } | null;
    bioShort: string | null;
    headshotKey: string | null;
    lat: number | null;
    lng: number | null;
    verified: boolean;
    acceptingClients: boolean;
}

function initialsFrom(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '??';
    }
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
}

function hueFromSlug(slug: string): number {
    let hash = 0;
    for (let i = 0; i < slug.length; i++) {
        hash = (hash * 31 + slug.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % 360;
}

export function toCardData(s: ServerLawyerCard): LawyerCardData {
    const base: LawyerCardData = {
        initials: initialsFrom(s.name),
        hue: hueFromSlug(s.slug),
        name: s.name,
        firm: s.firm ?? '',
        city: s.city ?? '',
        state: s.stateAbbr ?? s.state ?? '',
        years: s.yearsLicensed ?? 0,
        rating: s.reviewsAvg ?? 0,
        reviews: s.reviewsCount,
        areas: s.practiceAreas.map((p) => p.name),
        langs: s.languages.map((l) => l.toUpperCase()),
        phone: s.phone ?? '',
        contingency: s.feeStructure === 'contingency',
        featured: s.featured,
    };
    if (s.bioShort) {
        base.bio = s.bioShort;
    }
    if (s.headshotKey) {
        base.headshotKey = s.headshotKey;
    }
    return base;
}
